import { Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';

import { Observable, Subject, Subscription, filter, map, switchMap, take, takeUntil } from 'rxjs';
import { isNil, cloneDeep } from 'lodash';

import { AbstractDialogComponent } from '../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { buildSlideInAnimationTrigger } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { AppearCondition, AutocompleteCustomOption } from '../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';

import { BrandIdentityService } from '../../../Common/BrandIdentity/services/brand-identity.service';
import { BrandIdentity } from '../../../Common/BrandIdentity/models/brand-identity';
import {
    ManageBrandIdentitiesDialogComponent
} from '../../../Common/BrandIdentity/components/ManageBrandIdentitiesDialog/manage-brand-identities-dialog.component';

import { ScrollPanelComponent } from '../../../Common/Basic/components/ScrollPanel/scroll-panel.component';

import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { ResourceActionTypes } from '../../../Common/Basic/enums/resource-actions.enum';

import { BrandCompetitor } from '../../../api/brandIdentity/models/brand-identity.api.model';

import { ViMonitor } from '../../models/vi-monitor.model';
import { ViBrandIdentity } from '../../models/vi-brand-identity.model';

import { ViBrandCardService } from '../../services/vi-brand-card.service';
import { ViBrandIdentityCardComponent } from '../ViBrandIdentityCard/vi-brand-identity-card.component';
import { ViCompetitorsSetupPanelComponent, ViCompetitorsSetupPanelResult } from '../ViCompetitorsSetupPanel/vi-competitors-setup-panel.component';


@Component({
    selector: 'agpr-vi-brand-setup-panel',
    templateUrl: './vi-brand-setup-panel.component.html',
    styleUrls: ['./vi-brand-setup-panel.component.scss'],
    standalone: false
})
export class ViBrandSetupPanelComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @ViewChildren(ViBrandIdentityCardComponent) cards!: QueryList<ViBrandIdentityCardComponent>;
    @ViewChildren(ViBrandIdentityCardComponent, { read: ElementRef }) cardRefs!: QueryList<ElementRef>;
    @ViewChild('brandScrollPanel') brandScrollPanel?: ScrollPanelComponent;
    @ViewChild('competitorsScrollPanel') competitorsScrollPanel?: ScrollPanelComponent;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<any>;
    @Input() monitor: ViMonitor;

    readonly id: string = 'viBrandSetupPanel';
    readonly buttonTypes = ButtonTypesEnum;
    readonly severityEnum = messageSeverityEnum;
    readonly maxCompetitors: number = 11;

    readonly brandList$: Observable<BrandIdentity[]>;
    readonly brandListAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'VisibilityIntelligence.brandSetupPanel.brandField.createBrand',
            action: (name: string) => {
                const newBrand = new BrandIdentity();
                newBrand.name = name;

                const dialogClosed$ = this.openManageBrandsDialog(newBrand);

                let createdBrandId: number;
                this.brandIdentityService.actionCompleted$.pipe(
                    filter(resourceAction => resourceAction.actionType === ResourceActionTypes.CREATE && !resourceAction.error),
                    take(1),
                    takeUntil(dialogClosed$)
                ).subscribe(resourceAction => {
                    createdBrandId = resourceAction.resource?.id;
                });

                dialogClosed$.pipe(
                    take(1),
                    filter(() => !isNil(createdBrandId)),
                    switchMap(() => this.brandList$.pipe(
                        map(brands => brands.find(brand => brand.id === createdBrandId)),
                        filter(brand => !!brand),
                        take(1)
                    ))
                ).subscribe((createdBrand: BrandIdentity) => {
                    this.setSelectedBrand(createdBrand);
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    confirmed: boolean = false;
    hideSlideOverlay: boolean = false;
    subscriptions: Subscription[] = [];
    brands: BrandIdentity[] = [];

    // For the start panel
    selectedBrand: BrandIdentity | null = null;
    hasSelectedBrand: boolean = false;

    // For the main panel
    displayErrors: boolean = false;
    hasViBrandSetups: boolean = false;
    viBrand: ViBrandIdentity | null = null;
    isCompetitiveBenchmarking: boolean = false;
    viCompetitors: ViBrandIdentity[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        private brandIdentityService: BrandIdentityService,
        private localizationService: LocalizationService,
        private viBrandCardService: ViBrandCardService
    ) {
        super(coreDialogService, 'ViBrandSetupPanel');
        this.brandList$ = this.brandIdentityService.all$;
    }

    ngOnInit(): void {
        this.setViBrand(this.monitor?.brand);
        this.viCompetitors = cloneDeep(this.monitor?.competitors) ?? [];
        this.isCompetitiveBenchmarking = this.monitor?.isCompetitiveBenchmarking ?? false;

        this.subscriptions.push(
            this.brandList$.subscribe(brands => {
                this.brands = brands ?? [];

                const selectedBrandId = this.selectedBrand?.id;
                if (isNil(selectedBrandId)) {
                    return;
                }

                const updatedBrand = brands.find(brand => brand.id === selectedBrandId);
                if (updatedBrand) {
                    this.setSelectedBrand(updatedBrand);
                }
            })
        );
    }

    selectBrand(brand: BrandIdentity | string): void {
        if (!brand) {
            this.setSelectedBrand(null);
            return;
        }

        if (typeof brand === 'string') {
            if (!brand.trim()) {
                this.setSelectedBrand(null);
                return;
            }
            const newBrand = new BrandIdentity();
            newBrand.name = brand;
            this.setSelectedBrand(newBrand);
            return;
        }

        this.setSelectedBrand(brand);
    }

    manageBrandIdentity(): void {
        this.openManageBrandsDialog(this.selectedBrand ?? new BrandIdentity());
    }

    openManageBrandsDialog(brand: BrandIdentity): Observable<void> {
        this.brandIdentityService.setCurrentResource(brand);

        const dialogClosed$ = new Subject<void>();
        this.coreDialogService.open(ManageBrandIdentitiesDialogComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: buildSlideInAnimationTrigger()
            },
            outputs: {
                dialogClose: () => {
                    dialogClosed$.next();
                    this.brandIdentityService.setCurrentResource(new BrandIdentity());
                }
            }
        });

        return dialogClosed$.asObservable();
    }

    getStarted(): void {
        if (!this.hasSelectedBrand || !this.selectedBrand) {
            return;
        }

        this.coreDialogService.open(ViCompetitorsSetupPanelComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: buildSlideInAnimationTrigger(),
                brand: this.selectedBrand,
                monitor: this.monitor
            },
            outputs: {
                dialogClose: (result: ViCompetitorsSetupPanelResult | null) => {
                    if (!result) {
                        return;
                    }
                    this.isCompetitiveBenchmarking = result.isCompetitiveBenchmarking;
                    this.viCompetitors = this.toViCompetitors(result.competitors);
                    const viBrand = this.selectedBrand ? this.viBrandCardService.toViBrandIdentity(this.selectedBrand) : null;
                    this.setViBrand(viBrand);
                }
            }
        });
    }

    populateCompetitors(brand: BrandIdentity | null): void {
        if (!brand || !brand.id) {
            return;
        }

        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'viBrandSetupPanelPopulateCompetitors',
                heading: this.localizationService.transform('VisibilityIntelligence.brandSetupPanel.populateCompetitors.heading', { name: brand.name }),
                message: this.localizationService.transform('VisibilityIntelligence.brandSetupPanel.populateCompetitors.message', { name: brand.name }),
                cancelLabel: 'Core.button.noThanks',
                okLabel: 'VisibilityIntelligence.brandSetupPanel.populateCompetitors.confirm'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (!confirmed) {
                        return;
                    }
                    this.viCompetitors = this.toViCompetitors(brand.competitors ?? []);
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    addCompetitor(): void {
        if (this.viCompetitors.length >= this.maxCompetitors) {
            return;
        }
        this.viCompetitors = [...this.viCompetitors, { name: '', nameVariations: [], websites: [] } as ViBrandIdentity];

        // Wait for cardRefs to actually pick up the newly rendered card.
        this.subscriptions.push(
            this.cardRefs.changes.pipe(take(1)).subscribe(() => {
                requestAnimationFrame(() => {
                    const refs = this.cardRefs?.toArray() ?? [];
                    const lastRef = refs[refs.length - 1];
                    if (lastRef) {
                        ScrollPanelComponent.scrollElementIntoView(lastRef.nativeElement, undefined, { behavior: 'smooth', block: 'start' });
                    }
                });
            })
        );
    }

    getExcludedNames(index: number): string[] {
        const otherNames = this.viCompetitors
            .filter((_, i) => i !== index)
            .map(competitor => competitor.name)
            .filter((name): name is string => !!name?.trim());

        return this.viBrand?.name?.trim() ? [this.viBrand.name, ...otherNames] : otherNames;
    }

    removeCompetitor(index: number): void {
        this.viCompetitors = this.viCompetitors.filter((_, i) => i !== index);
    }

    cancel(): void {
        this.hideSlideOverlay = true;
    }

    done(): void {
        this.displayErrors = true;
        if (!this.validate()) {
            return;
        }

        this.monitor.brand = this.viBrand;
        this.monitor.isCompetitiveBenchmarking = this.isCompetitiveBenchmarking;
        this.monitor.competitors = this.isCompetitiveBenchmarking ? this.viCompetitors : [];

        this.confirmed = true;
        this.hideSlideOverlay = true;
    }

    onVisibleChange(visible: boolean): void {
        if (!visible) {
            this.closeDialog(this.confirmed);
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private setViBrand(value?: ViBrandIdentity | null): void {
        this.viBrand = cloneDeep(value);
        this.hasViBrandSetups = !!this.viBrand?.name?.trim();
    }

    private setSelectedBrand(value?: BrandIdentity | null): void {
        this.selectedBrand = value?.clone() ?? null;
        this.hasSelectedBrand = !!this.selectedBrand?.name?.trim();
    }

    private toViCompetitors(competitors: BrandCompetitor[]): ViBrandIdentity[] {
        return competitors.map(competitor => {
            const brand = this.brands.find(item => item.id === competitor?.id);
            return brand
                ? this.viBrandCardService.toViBrandIdentity(brand, true)
                : { name: competitor.name } as ViBrandIdentity;
        });
    }

    private validate(): boolean {
        const cards = this.cards?.toArray() ?? [];
        const isValid = cards.reduce((valid, card) => card.validate() && valid, true);

        if (!isValid) {
            this.scrollErrorsIntoView();
        }
        return isValid;
    }

    private scrollErrorsIntoView(): void {
        requestAnimationFrame(() => {
            const cards = this.cards?.toArray() ?? [];
            const hasError = (card: ViBrandIdentityCardComponent) =>
                card.hasNameError || card.hasDuplicateNameError || card.hasWebsitesError || card.hasIndustryError || card.hasBioError;

            const [mainCard, ...competitorCards] = cards;
            if (mainCard && hasError(mainCard)) {
                mainCard.scrollFirstErrorIntoView(this.brandScrollPanel);
            }

            const firstInvalidCompetitor = competitorCards.find(hasError);
            if (firstInvalidCompetitor) {
                firstInvalidCompetitor.scrollFirstErrorIntoView(this.competitorsScrollPanel);
            }
        });
    }
}
